import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, HpTag, SearchField } from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import {
  invalidatePeople,
  usePeopleCandidates,
  usePeopleOrgSearch,
  type PeopleCandidate,
  type PeopleOrg,
} from './queries';
import { ErrorAlert } from '../../components/error-alert';
import { PdLink } from '../../components/pd-link';

/**
 * Lusha decision-maker discovery: search an org, list ranked people for free,
 * reveal emails/phones per person (1 credit each), import into Pipedrive.
 */
export function PeopleFinder({ pdDomain }: { pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['enrichment', 'common']);
  const [query, setQuery] = useState('');
  const [org, setOrg] = useState<PeopleOrg | null>(null);
  const canSpend = useCan()('credits.spend');

  const search = usePeopleOrgSearch(org ? '' : query);
  const candidates = usePeopleCandidates(org?.pdId ?? null);

  const discover = useMutation(trpc.enrichment.people.discover.mutationOptions({ onSuccess: invalidatePeople }));

  const lushaConfigured = search.data?.lushaConfigured ?? true;
  const all = candidates.data ?? [];
  const visible = all.filter((c) => c.status !== 'dismissed');

  return (
    <section className="hp-card">
      <HpCardHeader title={t('people.title')} subtitle={t('people.subtitle')} icon="users">
        {org && (
          <HpButton
            icon="rotateCcw"
            onClick={() => {
              setOrg(null);
              setQuery('');
              discover.reset();
            }}
          >
            {t('people.changeOrg')}
          </HpButton>
        )}
      </HpCardHeader>

      {org === null ? (
        <div className="hp-card__body bo-sections">
          <SearchField
            className="bo-search"
            placeholder={t('people.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('people.searchAria')}
          />
          {!lushaConfigured && <p className="bo-hint bo-hint--danger">{t('people.notConfigured')}</p>}
          {search.isLoading && <p className="bo-muted">{t('people.searching')}</p>}
          <ErrorAlert error={search.error} />
          {search.data && search.data.items.length === 0 && <p className="bo-muted">{t('people.noOrgs')}</p>}
          {(search.data?.items.length ?? 0) > 0 && (
            <ul className="bo-picklist">
              {(search.data?.items ?? []).map((item) => (
                <li key={item.pdId}>
                  <button type="button" onClick={() => setOrg(item)}>
                    <strong>{item.name ?? `#${item.pdId}`}</strong>
                    {item.domain ? (
                      <span className="bo-mono bo-muted">{item.domain}</span>
                    ) : (
                      <HpTag tone="neutral" title={t('people.noDomainTitle')}>
                        {t('people.noDomain')}
                      </HpTag>
                    )}
                    <span className="bo-grow-end bo-row">
                      <span className="bo-chip">{t('common:count.deals', { count: item.openDeals })}</span>
                      {item.candidateCount > 0 && (
                        <HpTag tone="info">{t('people.peopleFound', { count: item.candidateCount })}</HpTag>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="hp-card__body bo-sections">
          <div className="bo-row">
            <strong>{org.name}</strong>
            {org.domain && <span className="bo-mono bo-muted">{org.domain}</span>}
            <PdLink domain={pdDomain} entity="organization" pdId={org.pdId} />
            <span className="bo-grow-end">
              <HpButton
                variant="primary"
                icon="search"
                busy={discover.isPending}
                disabled={!org.domain || !lushaConfigured || !canSpend}
                title={org.domain ? t('people.discoverFree') : t('people.discoverNoDomain')}
                onClick={() => discover.mutate({ orgPdId: org.pdId })}
              >
                {discover.isPending ? t('people.searching') : t('people.discover')}
              </HpButton>
            </span>
          </div>
          <ErrorAlert error={discover.error ?? candidates.error} />

          {candidates.isLoading && <p className="bo-muted">{t('common:actions.loading')}</p>}
          {!candidates.isLoading && visible.length === 0 && (
            <p className="bo-muted">
              {all.length > 0
                ? t('people.allDismissed')
                : discover.isSuccess
                  ? t('people.noneForDomain', { domain: org.domain ?? t('people.thisDomain') })
                  : t('people.notYet')}
            </p>
          )}
          {visible.length > 0 && (
            <ul className="bo-people">
              {visible.map((candidate) => (
                <CandidateCard key={candidate.id} candidate={candidate} pdDomain={pdDomain} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function CandidateCard({ candidate, pdDomain }: { candidate: PeopleCandidate; pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['enrichment', 'common']);
  const canSpend = useCan()('credits.spend');
  const reveal = useMutation(trpc.enrichment.people.reveal.mutationOptions({ onSuccess: invalidatePeople }));
  const importPd = useMutation(
    trpc.enrichment.people.importToPipedrive.mutationOptions({ onSuccess: invalidatePeople }),
  );
  const dismiss = useMutation(trpc.enrichment.people.dismiss.mutationOptions({ onSuccess: invalidatePeople }));

  const cost = (field: 'emails' | 'phones') => candidate.canReveal?.find((c) => c.field === field)?.credits;
  const costSuffix = (field: 'emails' | 'phones') => {
    const credits = cost(field);
    return credits === undefined ? t('people.notAvailable') : t('people.creditCost', { count: credits });
  };
  const active = candidate.status === 'found' || candidate.status === 'revealed';

  return (
    <li className="bo-person">
      <div className="bo-row">
        <strong>{candidate.name}</strong>
        {candidate.jobTitle && <span>{candidate.jobTitle}</span>}
        {candidate.seniority && <HpTag tone="warning">{candidate.seniority}</HpTag>}
        {candidate.department && <span className="bo-chip">{candidate.department}</span>}
        {candidate.linkedinUrl && (
          <a
            href={candidate.linkedinUrl}
            target="_blank"
            rel="noreferrer"
            className="hp-detail-action hp-detail-action--text"
          >
            LinkedIn ↗
          </a>
        )}
        {candidate.location && <span className="bo-muted">{candidate.location}</span>}
        {candidate.status === 'imported' && candidate.pdPersonId !== null && (
          <span className="bo-grow-end">
            <PdLink domain={pdDomain} entity="person" pdId={candidate.pdPersonId} />
          </span>
        )}
      </div>

      {(candidate.emails?.length ?? 0) + (candidate.phones?.length ?? 0) > 0 && (
        <p className="bo-row bo-mono">
          {(candidate.emails ?? []).map((e) => (
            <a key={e.email} href={`mailto:${e.email}`} className="hp-detail-action hp-detail-action--text">
              {e.email}
              {e.confidence && <span className="bo-muted"> ({e.confidence})</span>}
            </a>
          ))}
          {(candidate.phones ?? []).map((p) => (
            <span key={p.number}>{p.number}</span>
          ))}
        </p>
      )}

      {active && canSpend && (
        <div className="bo-row">
          {(candidate.emails?.length ?? 0) === 0 && (
            <HpButton
              className="bo-button--sm"
              variant="secondary"
              icon="mail"
              busy={reveal.isPending && reveal.variables?.fields[0] === 'emails'}
              disabled={reveal.isPending || cost('emails') === undefined}
              title={cost('emails') === undefined ? t('people.noEmail') : undefined}
              onClick={() => reveal.mutate({ id: candidate.id, fields: ['emails'] })}
            >
              {t('people.revealEmail')}
              {costSuffix('emails')}
            </HpButton>
          )}
          {(candidate.phones?.length ?? 0) === 0 && (
            <HpButton
              className="bo-button--sm"
              variant="secondary"
              icon="phone"
              busy={reveal.isPending && reveal.variables?.fields[0] === 'phones'}
              disabled={reveal.isPending || cost('phones') === undefined}
              title={cost('phones') === undefined ? t('people.noPhone') : undefined}
              onClick={() => reveal.mutate({ id: candidate.id, fields: ['phones'] })}
            >
              {t('people.revealPhone')}
              {costSuffix('phones')}
            </HpButton>
          )}
          <HpButton
            className="bo-button--sm"
            variant="primary"
            icon="plus"
            busy={importPd.isPending}
            onClick={() => importPd.mutate({ id: candidate.id })}
          >
            {importPd.isPending ? t('people.importing') : t('people.addToPipedrive')}
          </HpButton>
          <span className="bo-grow-end">
            <HpButton
              className="bo-button--sm"
              busy={dismiss.isPending}
              onClick={() => dismiss.mutate({ id: candidate.id })}
            >
              {t('common:actions.dismiss')}
            </HpButton>
          </span>
        </div>
      )}
      {reveal.data && !reveal.data.gotAnything && (
        <p className="bo-muted">{t('people.nothingRevealed', { count: reveal.data.creditsCharged })}</p>
      )}
      <ErrorAlert error={reveal.error ?? importPd.error ?? dismiss.error} />
    </li>
  );
}
